import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../Components/Redux/store';
import { setAuth } from '../Components/Redux/authSlice';
import { FaCheck, FaTimes, FaCalendarAlt, FaUsers, FaSave, FaSearch, FaFilter, FaUserCheck, FaChartLine } from 'react-icons/fa';

interface Student {
  id: number;
  name: string;
  Index_number: string;
}

interface Class {
  id: number;
  name: string;
}

interface AttendanceRecord {
  id: number;
  student: number;
  student_name: string;
  class_name: string;
  date: string;
  status: string;
  remarks: string;
  Index_number?: string;
  recorded_by?: string;
  recorded_by_name?: string;
  recorded_at?: string;
}

interface AttendanceState {
  [studentId: number]: {
    status: 'Present' | 'Absent' | 'Late' | '';
    remarks: string;
  };
}

const AttendanceTable: React.FC = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);

  // Form state
  const [selectedDate, setSelectedDate] = useState(getCurrentDate());
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [classes, setClasses] = useState<Class[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [attendanceState, setAttendanceState] = useState<AttendanceState>({});

  // View state
  const [activeTab, setActiveTab] = useState<'mark' | 'view'>('mark');
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [filterDate, setFilterDate] = useState(getCurrentDate());
  const [filterClass, setFilterClass] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [userRole, setUserRole] = useState('');

  function getCurrentDate() {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i].trim();
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length);
      }
    }
    return '';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const { data } = await axios.get('/users/user');
        setUserRole(data.user.role);
        dispatch(setAuth(true));
      } catch (e) {
        dispatch(setAuth(false));
      }
    };
    fetchData();
  }, [dispatch]);

  const fetchClasses = useCallback(async () => {
    try {
      const response = await axios.get('/Branches/classes/');
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  }, []);

  const fetchSections = useCallback(async () => {
    try {
      const response = await axios.get(`/Branches/sections/?class_id=${selectedClass}`);
      setSections(response.data);
    } catch (error) {
      console.error('Error fetching sections:', error);
      setSections([]);
    }
  }, [selectedClass]);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/Branches/students/?class_id=${selectedClass}`;
      if (selectedSection) {
        url += `&section_id=${selectedSection}`;
      }
      const response = await axios.get(url);

      // Handle both paginated and non-paginated responses
      let studentData: Student[] = [];
      if (Array.isArray(response.data)) {
        studentData = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        studentData = response.data.results;
      }

      setStudents(studentData);

      // Initialize attendance state for all students
      const initialState: AttendanceState = {};
      studentData.forEach((student: Student) => {
        initialState[student.id] = { status: '', remarks: '' };
      });
      setAttendanceState(initialState);
    } catch (error) {
      console.error('Error fetching students:', error);
      setMessage({ type: 'error', text: 'Failed to fetch students' });
    } finally {
      setLoading(false);
    }
  }, [selectedClass, selectedSection]);

  const fetchAttendanceRecords = useCallback(async () => {
    // Don't fetch if no class is selected
    if (!filterClass) {
      setAttendanceRecords([]);
      return;
    }

    setLoading(true);
    try {
      let url = `/Branches/attendance/?date=${filterDate}`;
      if (filterClass) {
        url += `&class_id=${filterClass}`;
      }
      console.log('Fetching attendance from:', url);
      const response = await axios.get(url);
      console.log('Attendance API Response:', response.data);

      // Ensure attendanceRecords is always an array
      let records = [];
      if (Array.isArray(response.data)) {
        records = response.data;
      } else if (response.data && Array.isArray(response.data.attendance_records)) {
        // The API returns records in 'attendance_records' property
        records = response.data.attendance_records;
      } else if (response.data && Array.isArray(response.data.results)) {
        records = response.data.results;
      } else if (response.data && typeof response.data === 'object') {
        // If it's an object, try to extract records from common property names
        records = response.data.data || response.data.records || [];
      }

      console.log('Parsed records:', records);
      console.log('Number of records:', records.length);
      setAttendanceRecords(records);
    } catch (error) {
      console.error('Error fetching attendance records:', error);
      setMessage({ type: 'error', text: 'Failed to fetch attendance records' });
      setAttendanceRecords([]); // Set to empty array on error
    } finally {
      setLoading(false);
    }
  }, [filterClass, filterDate]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    if (selectedClass) {
      fetchSections();
      fetchStudents();
    }
  }, [selectedClass, selectedSection, fetchSections, fetchStudents]);

  useEffect(() => {
    if (activeTab === 'view') {
      fetchAttendanceRecords();
    }
  }, [activeTab, filterDate, filterClass, fetchAttendanceRecords]);

  const handleStatusChange = (studentId: number, status: 'Present' | 'Absent' | 'Late') => {
    setAttendanceState(prev => ({
      ...prev,
      [studentId]: { ...prev[studentId], status }
    }));
  };

  const handleRemarksChange = (studentId: number, remarks: string) => {
    setAttendanceState(prev => ({
      ...prev,
      [studentId]: { ...prev[studentId], remarks }
    }));
  };

  const handleMarkAll = (status: 'Present' | 'Absent' | 'Late') => {
    const newState: AttendanceState = {};
    students.forEach(student => {
      newState[student.id] = { status, remarks: attendanceState[student.id]?.remarks || '' };
    });
    setAttendanceState(newState);
  };

  const handleSubmitAttendance = async () => {
    // Validate
    if (!selectedDate || !selectedClass) {
      setMessage({ type: 'error', text: 'Please select date and class' });
      return;
    }

    const hasRecords = Object.values(attendanceState).some(record => record.status !== '');
    if (!hasRecords) {
      setMessage({ type: 'error', text: 'Please mark attendance for at least one student' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const attendanceRecords = Object.entries(attendanceState)
        .filter(([_, record]) => record.status !== '')
        .map(([studentId, record]) => ({
          date: selectedDate,
          status: record.status,
          student: parseInt(studentId),
          class_associated: parseInt(selectedClass),
          remarks: record.remarks || ''
        }));

      // Submit attendance records
      await Promise.all(
        attendanceRecords.map(record =>
          axios.post('/Branches/attendance/', record)
        )
      );

      setMessage({ type: 'success', text: `Successfully recorded attendance for ${attendanceRecords.length} students!` });

      // Reset form
      const initialState: AttendanceState = {};
      students.forEach(student => {
        initialState[student.id] = { status: '', remarks: '' };
      });
      setAttendanceState(initialState);

      // Update filter date to match the date we just saved
      setFilterDate(selectedDate);
      if (selectedClass) {
        setFilterClass(selectedClass);
      }

      // Switch to view tab and refresh records
      setActiveTab('view');

      // Fetch the updated records
      setTimeout(() => {
        fetchAttendanceRecords();
      }, 500);

      // Auto-clear success message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    } catch (error: any) {
      console.error('Error saving attendance:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save attendance. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const filteredRecords = attendanceRecords.filter(record =>
    record.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.Index_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Statistics
  const presentCount = filteredRecords.filter(r => r.status === 'Present').length;
  const absentCount = filteredRecords.filter(r => r.status === 'Absent').length;
  const lateCount = filteredRecords.filter(r => r.status === 'Late').length;

  if (!auth) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <div className="container-fluid px-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
              <div>
                <h1 className="h2 fw-bold text-dark mb-2">📋 Attendance Management</h1>
                <p className="text-muted mb-0">Track and manage student attendance efficiently</p>
              </div>
              <div className="bg-white rounded-3 px-4 py-2 shadow-sm border">
                <div className="d-flex align-items-center gap-2">
                  <FaCalendarAlt className="text-primary" />
                  <span className="fw-semibold small text-dark">{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-0">
                <div className="d-flex">
                  <button
                    className={`flex-fill py-3 px-4 border-0 bg-transparent fw-semibold transition-all ${activeTab === 'mark'
                      ? 'text-primary border-bottom border-primary border-3'
                      : 'text-muted hover-bg-light'
                      }`}
                    onClick={() => setActiveTab('mark')}
                  >
                    <FaUserCheck className="me-2" />
                    Mark Attendance
                  </button>
                  <button
                    className={`flex-fill py-3 px-4 border-0 bg-transparent fw-semibold transition-all ${activeTab === 'view'
                      ? 'text-primary border-bottom border-primary border-3'
                      : 'text-muted hover-bg-light'
                      }`}
                    onClick={() => setActiveTab('view')}
                  >
                    <FaChartLine className="me-2" />
                    View Records
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div className="row mb-4">
            <div className="col-12">
              <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show border-0 shadow-sm`} role="alert">
                <i className={`bi bi-${message.type === 'success' ? 'check-circle' : 'exclamation-triangle'}-fill me-2`}></i>
                {message.text}
                <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
              </div>
            </div>
          </div>
        )}

        {/* Mark Attendance Tab */}
        {activeTab === 'mark' && (
          <div className="row">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-4">
                  <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                    <span className="bg-primary bg-opacity-10 text-primary rounded-circle p-2">
                      <FaCalendarAlt size={16} />
                    </span>
                    Select Class and Date
                  </h5>

                  <div className="row g-3 mb-4">
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label fw-semibold text-secondary small">Date</label>
                      <input
                        type="date"
                        className="form-control form-control-lg border-2"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        max={getCurrentDate()}
                      />
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label fw-semibold text-secondary small">Class</label>
                      <select
                        className="form-select form-select-lg border-2"
                        value={selectedClass}
                        onChange={(e) => {
                          setSelectedClass(e.target.value);
                          setSelectedSection('');
                        }}
                      >
                        <option value="">Select Class</option>
                        {classes.map(cls => (
                          <option key={cls.id} value={cls.id}>{cls.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <label className="form-label fw-semibold text-secondary small">Section (Optional)</label>
                      <select
                        className="form-select form-select-lg border-2"
                        value={selectedSection}
                        onChange={(e) => setSelectedSection(e.target.value)}
                        disabled={!selectedClass}
                      >
                        <option value="">All Sections</option>
                        {sections.map(section => (
                          <option key={section.id} value={section.id}>{section.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {students.length > 0 && (
                    <>
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4 pb-3 border-bottom">
                        <h6 className="fw-bold mb-0 d-flex align-items-center gap-2">
                          <FaUsers className="text-primary" />
                          Students ({students.length})
                        </h6>
                        <div className="d-flex gap-2 flex-wrap">
                          <button
                            className="btn btn-success btn-sm rounded-pill px-4"
                            onClick={() => handleMarkAll('Present')}
                          >
                            <FaCheck className="me-2" size={12} />
                            Mark All Present
                          </button>
                          <button
                            className="btn btn-warning btn-sm rounded-pill px-4 text-white"
                            onClick={() => handleMarkAll('Late')}
                          >
                            <FaCalendarAlt className="me-2" size={12} />
                            Mark All Late
                          </button>
                          <button
                            className="btn btn-danger btn-sm rounded-pill px-4"
                            onClick={() => handleMarkAll('Absent')}
                          >
                            <FaTimes className="me-2" size={12} />
                            Mark All Absent
                          </button>
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead className="bg-light">
                            <tr>
                              <th style={{ width: '50px' }} className="text-muted fw-semibold small">#</th>
                              <th className="text-muted fw-semibold small">STUDENT NAME</th>
                              <th className="text-muted fw-semibold small">INDEX NO.</th>
                              <th style={{ width: '220px' }} className="text-center text-muted fw-semibold small">STATUS</th>
                              <th style={{ width: '280px' }} className="text-muted fw-semibold small">REMARKS</th>
                            </tr>
                          </thead>
                          <tbody>
                            {students.map((student, index) => (
                              <tr key={student.id}>
                                <td className="text-muted small fw-bold">{index + 1}</td>
                                <td>
                                  <div className="fw-semibold text-dark">{student.name}</div>
                                </td>
                                <td className="text-muted small">{student.Index_number}</td>
                                <td>
                                  <div className="d-flex gap-2 justify-content-center flex-wrap">
                                    <button
                                      className={`btn btn-sm rounded-pill px-3 ${attendanceState[student.id]?.status === 'Present'
                                        ? 'btn-success shadow-sm'
                                        : 'btn-outline-success'
                                        }`}
                                      onClick={() => handleStatusChange(student.id, 'Present')}
                                    >
                                      <FaCheck size={11} className="me-1" />
                                      Present
                                    </button>
                                    <button
                                      className={`btn btn-sm rounded-pill px-3 ${attendanceState[student.id]?.status === 'Late'
                                        ? 'btn-warning shadow-sm text-white'
                                        : 'btn-outline-warning'
                                        }`}
                                      onClick={() => handleStatusChange(student.id, 'Late')}
                                    >
                                      <FaCalendarAlt size={11} className="me-1" />
                                      Late
                                    </button>
                                    <button
                                      className={`btn btn-sm rounded-pill px-3 ${attendanceState[student.id]?.status === 'Absent'
                                        ? 'btn-danger shadow-sm'
                                        : 'btn-outline-danger'
                                        }`}
                                      onClick={() => handleStatusChange(student.id, 'Absent')}
                                    >
                                      <FaTimes size={11} className="me-1" />
                                      Absent
                                    </button>
                                  </div>
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Optional..."
                                    value={attendanceState[student.id]?.remarks || ''}
                                    onChange={(e) => handleRemarksChange(student.id, e.target.value)}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                        <button
                          className="btn btn-primary btn-lg rounded-pill px-5 shadow"
                          onClick={handleSubmitAttendance}
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              Saving...
                            </>
                          ) : (
                            <>
                              <FaSave className="me-2" />
                              Save Attendance
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  )}

                  {selectedClass && students.length === 0 && !loading && (
                    <div className="text-center py-5">
                      <FaUsers size={48} className="text-muted mb-3" />
                      <p className="text-muted">No students found in this class</p>
                    </div>
                  )}

                  {!selectedClass && (
                    <div className="text-center py-5">
                      <FaFilter size={48} className="text-muted mb-3" />
                      <p className="text-muted">Please select a class to view students</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Records Tab */}
        {activeTab === 'view' && (
          <>
            {/* Statistics Cards */}
            {filteredRecords.length > 0 && (
              <div className="row g-3 mb-4">
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm bg-success bg-opacity-10">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <p className="text-success fw-semibold mb-1 small">Present</p>
                          <h3 className="fw-bold mb-0 text-success">{presentCount}</h3>
                        </div>
                        <div className="bg-success bg-opacity-25 rounded-circle p-3">
                          <FaCheck className="text-success" size={24} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm bg-warning bg-opacity-10">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <p className="text-warning fw-semibold mb-1 small">Late</p>
                          <h3 className="fw-bold mb-0 text-warning">{lateCount}</h3>
                        </div>
                        <div className="bg-warning bg-opacity-25 rounded-circle p-3">
                          <FaCalendarAlt className="text-warning" size={24} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm bg-danger bg-opacity-10">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <p className="text-danger fw-semibold mb-1 small">Absent</p>
                          <h3 className="fw-bold mb-0 text-danger">{absentCount}</h3>
                        </div>
                        <div className="bg-danger bg-opacity-25 rounded-circle p-3">
                          <FaTimes className="text-danger" size={24} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card border-0 shadow-sm bg-primary bg-opacity-10">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <p className="text-primary fw-semibold mb-1 small">Total Records</p>
                          <h3 className="fw-bold mb-0 text-primary">{filteredRecords.length}</h3>
                        </div>
                        <div className="bg-primary bg-opacity-25 rounded-circle p-3">
                          <FaUsers className="text-primary" size={24} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="row g-3 mb-4">
                      <div className="col-lg-4 col-md-6">
                        <label className="form-label fw-semibold text-secondary small">Filter by Date</label>
                        <input
                          type="date"
                          className="form-control form-control-lg border-2"
                          value={filterDate}
                          onChange={(e) => setFilterDate(e.target.value)}
                        />
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <label className="form-label fw-semibold text-secondary small">Filter by Class *</label>
                        <select
                          className="form-select form-select-lg border-2"
                          value={filterClass}
                          onChange={(e) => setFilterClass(e.target.value)}
                        >
                          <option value="">Select a Class</option>
                          {classes.map(cls => (
                            <option key={cls.id} value={cls.id}>{cls.name}</option>
                          ))}
                        </select>
                        <small className="text-muted">Required - Select a class to view attendance</small>
                      </div>
                      <div className="col-lg-4 col-md-6">
                        <label className="form-label fw-semibold text-secondary small">Search</label>
                        <div className="input-group input-group-lg">
                          <span className="input-group-text bg-white border-2">
                            <FaSearch className="text-muted" />
                          </span>
                          <input
                            type="text"
                            className="form-control border-2"
                            placeholder="Search by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            disabled={!filterClass}
                          />
                        </div>
                      </div>
                    </div>

                    {!filterClass ? (
                      <div className="text-center py-5">
                        <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex p-4 mb-3">
                          <FaFilter size={48} className="text-primary" />
                        </div>
                        <h5 className="fw-bold text-dark mb-2">Select a Class to View Attendance</h5>
                        <p className="text-muted">Please select a class from the dropdown above to view attendance records for that class.</p>
                      </div>
                    ) : loading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : filteredRecords.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead className="bg-light">
                            <tr>
                              <th style={{ width: '50px' }} className="text-muted fw-semibold small">#</th>
                              <th className="text-muted fw-semibold small">DATE</th>
                              <th className="text-muted fw-semibold small">STUDENT NAME</th>
                              <th className="text-muted fw-semibold small">CLASS</th>
                              <th style={{ width: '140px' }} className="text-center text-muted fw-semibold small">STATUS</th>
                              <th className="text-muted fw-semibold small">REMARKS</th>
                              {userRole !== 'teacher' && <th className="text-muted fw-semibold small">RECORDED BY</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRecords.map((record, index) => (
                              <tr key={record.id}>
                                <td className="text-muted small fw-bold">{index + 1}</td>
                                <td className="text-muted small">{new Date(record.date).toLocaleDateString()}</td>
                                <td className="fw-semibold text-dark">{record.student_name}</td>
                                <td className="text-muted small">{record.class_name}</td>
                                <td className="text-center">
                                  <span className={`badge ${record.status === 'Present' ? 'bg-success' :
                                    record.status === 'Late' ? 'bg-warning' :
                                      'bg-danger'
                                    } rounded-pill px-3 py-2`}>
                                    {record.status === 'Present' ? <FaCheck size={10} className="me-1" /> :
                                      record.status === 'Late' ? <FaCalendarAlt size={10} className="me-1" /> :
                                        <FaTimes size={10} className="me-1" />}
                                    {record.status}
                                  </span>
                                </td>
                                <td className="text-muted small">
                                  {record.remarks || <span className="fst-italic text-black-50">No remarks</span>}
                                </td>
                                {userRole !== 'teacher' && (
                                  <td>
                                    <div className="small">
                                      <div className="text-dark fw-semibold">{record.recorded_by_name || 'System'}</div>
                                      {record.recorded_at && (
                                        <div className="text-muted">{new Date(record.recorded_at).toLocaleString()}</div>
                                      )}
                                    </div>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <FaCalendarAlt size={48} className="text-muted mb-3" />
                        <p className="text-muted">No attendance records found</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AttendanceTable;
